import {
  WorkflowFormContext,
  IWorkflowFormActions,
  IWorkflowFormSubmissionAvailableAssigneePayload,
  IWorkflowFormSubmissionCommentFetchActionPayload,
  IWorkflowFormSubmissionCommentUpdateActionPayload,
  IWorkflowFormSubmissionFetchActionPayload,
  IWorkflowFormSubmissionCommentAttachmentAddActionPayload,
  IWorkflowFormSubmissionCommentAttachmentRemoveActionPayload
} from './actions.types';
import {
  IDepartmentData,
  IWorkflowFormSubmissionChecklistData,
  IWorkflowFormSubmissionCommentData,
  IWorkflowFormSubmissionData,
  IUserData,
  IWorkflowTransitionLogData,
  IWorkflowFormTemplateRevisionData,
  IWorkflowFormTemplateData
} from '@/models';
import { ITransition } from '@/models/transition';
import {
  IWorkflowFormPaginatedListData,
  IUpdateSubmissionChecklistAssigneePayload,
  IUpdateWorkflowFormSubmissionStatePayload,
  IWorkflowFormSubmissionFetchParams
} from '@/services';

export const actions: IWorkflowFormActions = {
  fetchWorkflowFormSubmissions(
    context: WorkflowFormContext,
    payload: IWorkflowFormSubmissionFetchActionPayload
  ): Promise<void> {
    const promise = this.$services.workflowForm.fetchWorkflowFormSubmissions(
      payload
    );

    context.commit('SET_LAST_PROMISE', promise);

    // Clear immediately ONLY when switching to a different template, so
    // debounced searches/filters within the same template keep showing rows.
    const incomingTemplateId = payload?.form__template
      ? payload.form__template
      : null;
    let currentTemplateId: number | null = null;
    if (context.state.workflowFormSubmissions.length > 0) {
      currentTemplateId = ((context.state.workflowFormSubmissions[0]
        .form as IWorkflowFormTemplateRevisionData)
        .template as IWorkflowFormTemplateData).id!;
    }

    if (
      incomingTemplateId !== null &&
      currentTemplateId !== null &&
      incomingTemplateId !== currentTemplateId
    ) {
      context.commit('SET_COUNT', 0);
      context.commit('SET_WORKFLOW_FORM_SUBMISSIONS', []);
    }

    return promise.then((paginated: IWorkflowFormPaginatedListData) => {
      if (promise === context.state.lastPromise) {
        context.commit('SET_COUNT', paginated.count);
        context.commit(
          'SET_WORKFLOW_FORM_SUBMISSIONS',
          paginated.results as IWorkflowFormSubmissionData[]
        );
        context.commit('SET_LAST_PROMISE', null);
      }
    });
  },

  fetchWorkflowFormSubmission(
    context: WorkflowFormContext,
    payload: number
  ): Promise<void> {
    return this.$services.workflowForm
      .fetchWorkflowFormSubmission(payload)
      .then((workflowForm: IWorkflowFormSubmissionData) => {
        const index = context.state.workflowFormSubmissions.findIndex(
          (v: IWorkflowFormSubmissionData) => v.id === workflowForm.id
        );
        if (index === -1) {
          context.commit('ADD_WORKFLOW_FORM_SUBMISSION', workflowForm);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_WORKFLOW_FORM_SUBMISSION', workflowForm);
        }
      });
  },

  updateWorkflowFormSubmissionState(
    context: WorkflowFormContext,
    payload: IUpdateWorkflowFormSubmissionStatePayload
  ): Promise<void> {
    return this.$services.workflowForm
      .updateWorkflowFormSubmissionState(payload)
      .then((workflowForm: IWorkflowFormSubmissionData) => {
        const index = context.state.workflowFormSubmissions.findIndex(
          (v: IWorkflowFormSubmissionData) => v.id === workflowForm.id
        );
        if (index === -1) {
          context.commit('ADD_WORKFLOW_FORM_SUBMISSION', workflowForm);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_WORKFLOW_FORM_SUBMISSION', workflowForm);
        }
      });
  },

  createWorkflowFormSubmission(
    context: WorkflowFormContext,
    payload: IWorkflowFormSubmissionData
  ): Promise<number> {
    return this.$services.workflowForm
      .createWorkflowFormSubmission(payload)
      .then((workflowForm: IWorkflowFormSubmissionData) => {
        context.commit('ADD_WORKFLOW_FORM_SUBMISSION', workflowForm);
        context.commit('INCREMENT_COUNT');

        return workflowForm.id!;
      });
  },

  updateWorkflowFormSubmission(
    context: WorkflowFormContext,
    payload: IWorkflowFormSubmissionData
  ): Promise<void> {
    return this.$services.workflowForm
      .updateWorkflowFormSubmission(payload)
      .then((workflowForm: IWorkflowFormSubmissionData) => {
        const index = context.state.workflowFormSubmissions.findIndex(
          (v: IWorkflowFormSubmissionData) => v.id === workflowForm.id
        );
        if (index === -1) {
          context.commit('ADD_WORKFLOW_FORM_SUBMISSION', workflowForm);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_WORKFLOW_FORM_SUBMISSION', workflowForm);
        }
      });
  },

  deleteWorkflowFormSubmission(
    context: WorkflowFormContext,
    payload: number
  ): Promise<void> {
    return this.$services.workflowForm
      .deleteWorkflowFormSubmission(payload)
      .then(() => {
        context.commit('REMOVE_WORKFLOW_FORM_SUBMISSION', payload);
        context.commit('DECREMENT_COUNT');
      });
  },

  fetchWorkflowFormSubmissionValidStateTransitions(
    context: WorkflowFormContext,
    payload: number
  ): Promise<void> {
    return this.$services.workflowForm
      .fetchWorkflowFormSubmissionValidStateTransitions(payload)
      .then(transitions => {
        context.commit('SET_WORKFLOW_FORM_SUBMISSION_TRANSITIONS', transitions);
      });
  },

  fetchWorkflowFormSubmissionsAvailableAssignees(
    context: WorkflowFormContext,
    payload: IWorkflowFormSubmissionAvailableAssigneePayload
  ): Promise<void> {
    return this.$services.workflowForm
      .fetchWorkflowFormSubmissionsAvailableAssignees(
        payload.submissionId,
        payload.stateId
      )
      .then(assignees => {
        context.commit(
          'SET_WORKFLOW_FORM_SUBMISSION_AVAILABLE_ASSIGNEES',
          assignees
        );
      });
  },

  fetchWorkflowFormSubmissionChecklists(
    context: WorkflowFormContext,
    payload: IWorkflowFormSubmissionData
  ): Promise<void> {
    return this.$services.workflowForm
      .fetchWorkflowFormSubmissionChecklists(payload)
      .then((checklist: IWorkflowFormSubmissionChecklistData[]) => {
        context.commit('SET_WORKFLOW_FORM_CHECKLISTS', checklist);
      });
  },

  updateWorkflowFormSubmissionChecklist(
    context: WorkflowFormContext,
    payload: IWorkflowFormSubmissionChecklistData
  ): Promise<void> {
    return this.$services.workflowForm
      .updateWorkflowFormSubmissionChecklist(payload)
      .then((checklist: IWorkflowFormSubmissionChecklistData) => {
        const index = context.state.workflowFormChecklists.findIndex(
          (v: IWorkflowFormSubmissionChecklistData) => v.id === checklist.id
        );
        if (index === -1) {
          context.commit('ADD_WORKFLOW_FORM_CHECKLIST', checklist);
        } else {
          context.commit('UPDATE_WORKFLOW_FORM_CHECKLIST', checklist);
        }
      });
  },

  markCompleteWorkflowFormSubmissionChecklist(
    context: WorkflowFormContext,
    payload: IWorkflowFormSubmissionChecklistData
  ): Promise<void> {
    return this.$services.workflowForm
      .markCompleteWorkflowFormSubmissionChecklist(payload)
      .then((checklist: IWorkflowFormSubmissionChecklistData) => {
        const index = context.state.workflowFormChecklists.findIndex(
          (v: IWorkflowFormSubmissionChecklistData) => v.id === checklist.id
        );
        if (index === -1) {
          context.commit('ADD_WORKFLOW_FORM_CHECKLIST', checklist);
        } else {
          context.commit('UPDATE_WORKFLOW_FORM_CHECKLIST', checklist);
        }
      });
  },

  updateWorkflowFormSubmissionChecklistAssignee(
    context: WorkflowFormContext,
    payload: IUpdateSubmissionChecklistAssigneePayload
  ): Promise<void> {
    return this.$services.workflowForm
      .updateWorkflowFormSubmissionChecklistAssignee(payload)
      .then((checklist: IWorkflowFormSubmissionChecklistData) => {
        const index = context.state.workflowFormChecklists.findIndex(
          (v: IWorkflowFormSubmissionChecklistData) => v.id === checklist.id
        );
        if (index === -1) {
          context.commit('ADD_WORKFLOW_FORM_CHECKLIST', checklist);
        } else {
          context.commit('UPDATE_WORKFLOW_FORM_CHECKLIST', checklist);
        }
      });
  },

  fetchChecklistAssigneeOptions(
    context: WorkflowFormContext,
    payload: IWorkflowFormSubmissionChecklistData
  ): Promise<IUserData[]> {
    return this.$services.workflowForm.fetchChecklistAssigneeOptions(payload);
  },

  fetchWorkflowFormSubmissionDepartments(
    context: WorkflowFormContext,
    payload: IWorkflowFormSubmissionData
  ): Promise<void> {
    return this.$services.workflowForm
      .fetchWorkflowFormSubmissionDepartments(payload)
      .then((departments: IDepartmentData[]) => {
        context.commit('SET_WORKFLOW_FORM_DEPARTMENTS', departments);
      });
  },

  //comment
  fetchWorkflowFormSubmissionComments(
    context: WorkflowFormContext,
    payload: number
  ): Promise<void> {
    return this.$services.workflowForm
      .fetchWorkflowFormSubmissionComments(payload)
      .then((comments: IWorkflowFormSubmissionCommentData[]) => {
        context.commit('SET_WORKFLOW_FORM_SUBMISSION_COMMENTS', comments);
      });
  },

  fetchWorkflowFormSubmissionComment(
    context: WorkflowFormContext,
    payload: IWorkflowFormSubmissionCommentFetchActionPayload
  ): Promise<void> {
    return this.$services.workflowForm
      .fetchWorkflowFormSubmissionComment(
        payload.submissionId,
        payload.commentId
      )
      .then((comment: IWorkflowFormSubmissionCommentData) => {
        const index = context.state.comments.findIndex(
          comment => comment.id === comment.id
        );
        if (index === -1) {
          context.commit('ADD_WORKFLOW_FORM_SUBMISSION_COMMENT', comment);
        } else {
          context.commit('UPDATE_WORKFLOW_FORM_SUBMISSION_COMMENT', comment);
        }
      });
  },

  createWorkflowFormSubmissionComment(
    context: WorkflowFormContext,
    payload: IWorkflowFormSubmissionCommentUpdateActionPayload
  ): Promise<number> {
    return this.$services.workflowForm
      .createWorkflowFormSubmissionComment(
        payload.submissionId,
        payload.comment
      )
      .then((comment: IWorkflowFormSubmissionCommentData) => {
        context.commit('ADD_WORKFLOW_FORM_SUBMISSION_COMMENT', comment);
        return comment.id!;
      });
  },

  updateWorkflowFormSubmissionComment(
    context: WorkflowFormContext,
    payload: IWorkflowFormSubmissionCommentUpdateActionPayload
  ): Promise<void> {
    return this.$services.workflowForm
      .updateWorkflowFormSubmissionComment(
        payload.submissionId,
        payload.comment
      )
      .then((comment: IWorkflowFormSubmissionCommentData) => {
        const index = context.state.comments.findIndex(
          comment => comment.id === comment.id
        );
        if (index === -1) {
          context.commit('ADD_WORKFLOW_FORM_SUBMISSION_COMMENT', comment);
        } else {
          context.commit('UPDATE_WORKFLOW_FORM_SUBMISSION_COMMENT', comment);
        }
      });
  },

  addWorkflowFormSubmissionCommentAttachments(
    context: WorkflowFormContext,
    payload: IWorkflowFormSubmissionCommentAttachmentAddActionPayload
  ): Promise<void> {
    return this.$services.workflowForm
      .addWorkflowFormSubmissionCommentAttachments(
        payload.submissionId,
        payload.commentId,
        payload.attachments
      )
      .then((comment: IWorkflowFormSubmissionCommentData) => {
        const index = context.state.comments.findIndex(
          comment => comment.id === payload.commentId
        );
        if (index === -1) {
          context.commit('ADD_WORKFLOW_FORM_SUBMISSION_COMMENT', comment);
        } else {
          context.commit('UPDATE_WORKFLOW_FORM_SUBMISSION_COMMENT', comment);
        }
      });
  },

  removeWorkflowFormSubmissionCommentAttachment(
    context: WorkflowFormContext,
    payload: IWorkflowFormSubmissionCommentAttachmentRemoveActionPayload
  ): Promise<void> {
    return this.$services.workflowForm
      .removeWorkflowFormSubmissionCommentAttachment(
        payload.submissionId,
        payload.commentId,
        payload.attachmentId
      )
      .then(() => {
        const index = context.state.comments.findIndex(
          comment => comment.id === payload.commentId
        );

        if (index !== -1) {
          const comment = context.state.comments[index];

          const attachmentIndex = comment.attachments!.findIndex(
            attachment => attachment.id === payload.attachmentId
          );

          comment.attachments!.splice(attachmentIndex, 1);

          context.commit('UPDATE_WORKFLOW_FORM_SUBMISSION_COMMENT', comment);
        }
      });
  },

  deleteWorkflowFormSubmissionComment(
    context: WorkflowFormContext,
    payload: IWorkflowFormSubmissionCommentFetchActionPayload
  ): Promise<void> {
    return this.$services.workflowForm
      .deleteWorkflowFormSubmissionComment(
        payload.submissionId,
        payload.commentId
      )
      .then(() => {
        context.commit(
          'REMOVE_WORKFLOW_FORM_SUBMISSION_COMMENT',
          payload.commentId
        );
      });
  },

  // ACTIVITIES
  fetchWorkflowFormSubmissionActivities(
    context: WorkflowFormContext,
    payload: number
  ): Promise<void> {
    return this.$services.workflowForm
      .fetchWorkflowFormSubmissionActivities(payload)
      .then((activities: IWorkflowTransitionLogData[]) => {
        context.commit('SET_WORKFLOW_FORM_SUBMISSION_ACTIVITIES', activities);
      });
  },

  // Mentions
  fetchWorkflowFormSubmissionCommentsMentionableUsers(
    context: WorkflowFormContext,
    payload: number
  ): Promise<void> {
    return this.$services.workflowForm
      .fetchWorkflowFormSubmissionCommentsMentionableUsers(payload)
      .then((mentionableUsers: IUserData[]) => {
        context.commit('SET_WORKFLOW_FORM_MENTIONABLE_USERS', mentionableUsers);
      });
  },

  exportWorkflowFormSubmissionPDF(
    context: WorkflowFormContext,
    payload: number
  ): Promise<Blob> {
    return this.$services.workflowForm.exportWorkflowFormSubmissionPDF(payload);
  },

  exportWorkflowFormSubmissionXLSX(
    _context: WorkflowFormContext,
    payload: IWorkflowFormSubmissionFetchParams
  ): Promise<Blob> {
    return this.$services.workflowForm.exportWorkflowFormSubmissionXLSX(
      payload
    );
  }
};
